import { __decorate, __metadata } from 'tslib';
import { NavigationCancel, NavigationError, Router, RoutesRecognized } from '@angular/router';
import { Action, Selector, State, Store, NgxsModule } from '@ngxs/store';
import { of } from 'rxjs';
import { NgZone, NgModule } from '@angular/core';

var RouterStateSerializer = /** @class */ (function () {
    function RouterStateSerializer() {
    }
    return RouterStateSerializer;
}());
var DefaultRouterStateSerializer = /** @class */ (function () {
    function DefaultRouterStateSerializer() {
    }
    DefaultRouterStateSerializer.prototype.serialize = function (routerState) {
        return {
            root: this.serializeRoute(routerState.root),
            url: routerState.url
        };
    };
    DefaultRouterStateSerializer.prototype.serializeRoute = function (route) {
        var _this = this;
        var children = route.children.map(function (c) { return _this.serializeRoute(c); });
        return {
            url: route.url,
            params: route.params,
            queryParams: route.queryParams,
            fragment: route.fragment,
            data: route.data,
            outlet: route.outlet,
            component: undefined,
            routeConfig: null,
            root: undefined,
            parent: undefined,
            firstChild: children[0],
            children: children,
            pathFromRoot: undefined,
            paramMap: route.paramMap,
            queryParamMap: route.queryParamMap,
            toString: route.toString
        };
    };
    return DefaultRouterStateSerializer;
}());

/**
 * Public event api of the router
 */
var Navigate = /** @class */ (function () {
    function Navigate(path, queryParams, extras) {
        this.path = path;
        this.queryParams = queryParams;
        this.extras = extras;
    }
    return Navigate;
}());
Navigate.type = '[Router] Navigate';
/**
 *
 * Angular Routers internal state events
 *
 */
/**
 * An action dispatched when the router navigates.
 */
var RouterNavigation = /** @class */ (function () {
    function RouterNavigation(routerState, event) {
        this.routerState = routerState;
        this.event = event;
    }
    return RouterNavigation;
}());
RouterNavigation.type = '[Router] RouterNavigation';
/**
 * An action dispatched when the router cancel navigation.
 */
var RouterCancel = /** @class */ (function () {
    function RouterCancel(routerState, storeState, event) {
        this.routerState = routerState;
        this.storeState = storeState;
        this.event = event;
    }
    return RouterCancel;
}());
RouterCancel.type = '[Router] RouterCancel';
/**
 * An action dispatched when the router errors.
 */
var RouterError = /** @class */ (function () {
    function RouterError(routerState, storeState, event) {
        this.routerState = routerState;
        this.storeState = storeState;
        this.event = event;
    }
    return RouterError;
}());
RouterError.type = '[Router] RouterError';

var RouterState = RouterState_1 = /** @class */ (function () {
    function RouterState(_store, _router, _serializer, _ngZone) {
        this._store = _store;
        this._router = _router;
        this._serializer = _serializer;
        this._ngZone = _ngZone;
        this.dispatchTriggeredByRouter = false;
        this.navigationTriggeredByDispatch = false;
        this.setUpRouterHook();
        this.setUpStoreListener();
        this.setUpStateRollbackEvents();
    }
    /**
       * Selectors
       */
    RouterState.state = function (state) {
        return state && state.state;
    };
    RouterState.url = function (state) {
        return state && state.state && state.state.url;
    };
    RouterState.prototype.navigate = function (ctx, action) {
        var _this = this;
        this._ngZone.run(function () { return _this._router.navigate(action.path, Object.assign({ queryParams: action.queryParams }, action.extras)); });
    };
    RouterState.prototype.angularRouterAction = function (ctx, action) {
        ctx.setState(Object.assign({}, ctx.getState(), { state: action.routerState, navigationId: action.event.id }));
    };
    /**
       * Hook into the angular router before each navigation action is performed
       * since the route tree can be large, we serialize it into something more manageable
       */
    RouterState.prototype.setUpRouterHook = function () {
        var _this = this;
        this._router.hooks.beforePreactivation = function (routerStateSnapshot) {
            _this.routerStateSnapshot = _this._serializer.serialize(routerStateSnapshot);
            if (_this.shouldDispatchRouterNavigation())
                _this.dispatchRouterNavigation();
            return of(true);
        };
    };
    RouterState.prototype.setUpStoreListener = function () {
        var _this = this;
        this._store.select(RouterState_1).subscribe(function (s) {
            _this.routerState = s;
        });
        this._store.select(RouterState_1.state).subscribe(function () {
            _this.navigateIfNeeded();
        });
    };
    RouterState.prototype.setUpStateRollbackEvents = function () {
        var _this = this;
        this._router.events.subscribe(function (e) {
            if (e instanceof RoutesRecognized) {
                _this.lastRoutesRecognized = e;
            }
            else if (e instanceof NavigationCancel) {
                _this.dispatchRouterCancel(e);
            }
            else if (e instanceof NavigationError) {
                _this.dispatchRouterError(e);
            }
        });
    };
    RouterState.prototype.shouldDispatchRouterNavigation = function () {
        if (!this.routerState)
            return true;
        return !this.navigationTriggeredByDispatch;
    };
    RouterState.prototype.navigateIfNeeded = function () {
        var _this = this;
        if (!this.routerState || !this.routerState.state) {
            return;
        }
        if (this.dispatchTriggeredByRouter)
            return;
        if (this._router.url !== this.routerState.state.url) {
            this.navigationTriggeredByDispatch = true;
            this._ngZone.run(function () { return _this._router.navigateByUrl(_this.routerState.state.url); });
        }
    };
    RouterState.prototype.dispatchRouterNavigation = function () {
        this.dispatchRouterAction(new RouterNavigation(this.routerStateSnapshot, new RoutesRecognized(this.lastRoutesRecognized.id, this.lastRoutesRecognized.url, this.lastRoutesRecognized.urlAfterRedirects, this.routerStateSnapshot)));
    };
    RouterState.prototype.dispatchRouterCancel = function (event) {
        this.dispatchRouterAction(new RouterCancel(this.routerStateSnapshot, this.routerState, event));
    };
    RouterState.prototype.dispatchRouterError = function (event) {
        this.dispatchRouterAction(new RouterError(this.routerStateSnapshot, this.routerState, new NavigationError(event.id, event.url, "" + event)));
    };
    RouterState.prototype.dispatchRouterAction = function (action) {
        this.dispatchTriggeredByRouter = true;
        try {
            this._store.dispatch(action);
        }
        finally {
            this.dispatchTriggeredByRouter = false;
            this.navigationTriggeredByDispatch = false;
        }
    };
    return RouterState;
}());
__decorate([
    Action(Navigate),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, Navigate]),
    __metadata("design:returntype", void 0)
], RouterState.prototype, "navigate", null);
__decorate([
    Action([RouterNavigation, RouterError, RouterCancel]),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, Object]),
    __metadata("design:returntype", void 0)
], RouterState.prototype, "angularRouterAction", null);
__decorate([
    Selector(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", void 0)
], RouterState, "state", null);
__decorate([
    Selector(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", String)
], RouterState, "url", null);
RouterState = RouterState_1 = __decorate([
    State({
        name: 'router',
        defaults: {
            state: null,
            navigationId: null
        }
    }),
    __metadata("design:paramtypes", [Store,
        Router,
        RouterStateSerializer,
        NgZone])
], RouterState);
var RouterState_1;

// @dynamic
var NgxsRouterPluginModule = /** @class */ (function () {
    function NgxsRouterPluginModule() {
    }
    NgxsRouterPluginModule.forRoot = function () {
        return {
            ngModule: NgxsRouterPluginModule,
            providers: []
        };
    };
    return NgxsRouterPluginModule;
}());
NgxsRouterPluginModule.decorators = [
    { type: NgModule, args: [{
                imports: [
                    // NOTE: Must mark as `dynamic` due to
                    // https://github.com/dherges/ng-packagr/issues/767
                    NgxsModule.forFeature([RouterState])
                ],
                providers: [{ provide: RouterStateSerializer, useClass: DefaultRouterStateSerializer }]
            },] },
];

/**
 * Generated bundle index. Do not edit.
 */

export { NgxsRouterPluginModule, RouterState, RouterStateSerializer, DefaultRouterStateSerializer, Navigate, RouterNavigation, RouterCancel, RouterError };
//# sourceMappingURL=ngxs-router-plugin.js.map
